import { useState, useEffect } from "react";
import type { User } from "@/lib/types";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { StarRating } from "@/components/StarRating";
import { Trophy, Star, GitFork, Box, TrendingUp, Medal, Crown, Award } from "lucide-react";
import { cn } from "@/lib/utils";

const rankIcons = [Crown, Medal, Award];
const rankColors = [
	"from-amber-400 to-yellow-600",
	"from-gray-300 to-gray-500",
	"from-orange-400 to-orange-700",
];

export function LeaderboardPage() {
	const [users, setUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		api.leaderboard()
			.then(setUsers)
			.finally(() => setLoading(false));
	}, []);

	return (
		<div className="max-w-3xl mx-auto space-y-6">
			{/* Header */}
			<div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-transparent border border-border/50 p-6 sm:p-8">
				<div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 h-64 w-64 rounded-full bg-amber-500/10 blur-3xl" />
				<div className="relative">
					<div className="flex items-center gap-2 mb-2">
						<Trophy className="h-5 w-5 text-amber-400" />
						<span className="text-xs font-semibold uppercase tracking-widest text-amber-400">Leaderboard</span>
					</div>
					<h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
						Top <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-600">Makers</span>
					</h1>
					<p className="mt-2 text-sm text-muted-foreground max-w-lg">
						Reputation is earned through uploads, forks, and community ratings.
					</p>
				</div>
			</div>

			{/* Leaderboard */}
			{loading ? (
				<div className="space-y-3">
					{[1, 2, 3, 4, 5].map((i) => (
						<div key={i} className="h-20 rounded-xl border border-border/50 bg-card/30 animate-pulse" />
					))}
				</div>
			) : (
				<div className="space-y-3">
					{users.map((user, index) => {
						const isTop3 = index < 3;
						const RankIcon = isTop3 ? rankIcons[index] : TrendingUp;

						return (
							<Card
								key={user.id}
								className={cn(
									"border-border/50 bg-card/50 overflow-hidden transition-all hover:border-border",
									isTop3 && "ring-1 ring-amber-500/20"
								)}
							>
								<CardContent className="p-4">
									<div className="flex items-center gap-4">
										{/* Rank */}
										<div className={cn(
											"h-10 w-10 rounded-xl flex items-center justify-center shrink-0",
											isTop3
												? `bg-gradient-to-br ${rankColors[index]} text-white shadow-lg`
												: "bg-muted/50 text-muted-foreground"
										)}>
											{isTop3 ? (
												<RankIcon className="h-5 w-5" />
											) : (
												<span className="text-sm font-bold">{index + 1}</span>
											)}
										</div>

										{/* Avatar */}
										<div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-sm font-bold text-white shrink-0">
											{user.displayName[0]}
										</div>

										{/* Info */}
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2">
												<span className="text-sm font-semibold">{user.displayName}</span>
												<span className="text-xs text-muted-foreground">@{user.username}</span>
											</div>
											<p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{user.bio}</p>
										</div>

										{/* Stats */}
										<div className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground shrink-0">
											<div className="flex flex-col items-center gap-0.5">
												<Box className="h-3.5 w-3.5" />
												<span>{user.projectCount}</span>
											</div>
											<div className="flex flex-col items-center gap-0.5">
												<GitFork className="h-3.5 w-3.5" />
												<span>{user.forkCount}</span>
											</div>
											<div className="flex flex-col items-center gap-0.5">
												<Star className="h-3.5 w-3.5" />
												<span>{user.averageRating.toFixed(1)}</span>
											</div>
										</div>

										{/* Reputation Score */}
										<div className="text-right shrink-0">
											<Badge
												className={cn(
													"text-xs font-bold",
													isTop3
														? "bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 border-amber-500/30"
														: "bg-muted/50"
												)}
											>
												{user.reputation} REP
											</Badge>
										</div>
									</div>
								</CardContent>
							</Card>
						);
					})}
				</div>
			)}

			{/* How Reputation Works */}
			<Card className="border-border/50 bg-card/50">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm flex items-center gap-2">
						<TrendingUp className="h-4 w-4 text-orange-400" />
						How Reputation Works
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
						<div className="flex items-start gap-2">
							<div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
								<Box className="h-4 w-4 text-emerald-400" />
							</div>
							<div>
								<p className="font-medium">Upload Projects</p>
								<p className="text-xs text-muted-foreground">+10 REP per upload</p>
							</div>
						</div>
						<div className="flex items-start gap-2">
							<div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
								<Star className="h-4 w-4 text-blue-400" />
							</div>
							<div>
								<p className="font-medium">Receive Ratings</p>
								<p className="text-xs text-muted-foreground">+1-5 REP per rating star</p>
							</div>
						</div>
						<div className="flex items-start gap-2">
							<div className="h-8 w-8 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
								<GitFork className="h-4 w-4 text-purple-400" />
							</div>
							<div>
								<p className="font-medium">Get Forked</p>
								<p className="text-xs text-muted-foreground">REP grows with community use</p>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
